import React, { useState } from 'react';
import {
  Modal, BlockStack, InlineStack, Text, TextField, Select, Button, Badge,
} from '@shopify/polaris';
import { Mail, Phone, Instagram, MapPin } from 'lucide-react';
import {
  WhatsAppIcon, MessengerIcon, InstagramIcon as _LogoInstaIcon, XIcon, TelegramIcon,
  TikTokIcon, SMSIcon, LineIcon, ViberIcon, SnapchatIcon, WeChatIcon, LinkedInIcon,
} from '@reach/ui/icons';
import { tierIndex } from '@reach/core';
import { ReachLogo } from './Logo';

// ---- Exported result type (consumed by Settings.tsx) ----
export interface WizardResult {
  channelId: string;
  channelValue: string;
  anchorColor: string;
  timezone?: string;
  startHour?: number;
  endHour?: number;
  botName?: string;
  welcomeMsg?: string;
}

interface OnboardingWizardProps {
  planTier: string;
  shopDomain: string;
  onComplete: (result: WizardResult) => void;
  onDismiss: () => void;
}


// Lucide wrappers for consistent size prop interface
const MailIcon = ({ size = 24 }: { size?: number }) => <Mail size={size} />;
const PhoneIcon = ({ size = 24 }: { size?: number }) => <Phone size={size} />;
const MapPinIcon = ({ size = 24 }: { size?: number }) => <MapPin size={size} />;
const InstagramLucideIcon = ({ size = 24 }: { size?: number }) => <Instagram size={size} />;

// ---- Channel list ----
type ChannelDef = {
  id: string;
  label: string;
  color: string;
  placeholder: string;
  fieldLabel: string;
  Icon: React.FC<{ size?: number }>;
};

const CHANNEL_LIST: ChannelDef[] = [
  { id: 'whatsapp', label: 'WhatsApp', color: '#25D366', placeholder: '+1 (555) 000-0000', fieldLabel: 'WhatsApp number (+1234…)', Icon: WhatsAppIcon },
  { id: 'messenger', label: 'Messenger', color: '#0084FF', placeholder: 'my-store-page', fieldLabel: 'Facebook Page ID', Icon: MessengerIcon },
  { id: 'instagram', label: 'Instagram', color: '#E4405F', placeholder: '@my_store', fieldLabel: 'Instagram Username', Icon: InstagramLucideIcon },
  { id: 'email', label: 'Email', color: '#EA4335', placeholder: 'support@yourstore.com', fieldLabel: 'Support Email', Icon: MailIcon },
  { id: 'phone', label: 'Phone', color: '#0EA5E9', placeholder: '+1 (555) 000-0000', fieldLabel: 'Phone Number', Icon: PhoneIcon },
  { id: 'telegram', label: 'Telegram', color: '#26A5E4', placeholder: '@my_store', fieldLabel: 'Telegram Username', Icon: TelegramIcon },
  { id: 'sms', label: 'SMS', color: '#2563EB', placeholder: '+1 (555) 000-0000', fieldLabel: 'Mobile Number', Icon: SMSIcon },
  { id: 'twitter', label: 'X (Twitter)', color: '#111111', placeholder: '@my_store', fieldLabel: 'X / Twitter username', Icon: XIcon },
  { id: 'google_maps', label: 'Maps', color: '#4285F4', placeholder: '123 Main St', fieldLabel: 'Shop address', Icon: MapPinIcon },
  { id: 'tiktok', label: 'TikTok', color: '#010101', placeholder: '@my_store', fieldLabel: 'TikTok username', Icon: TikTokIcon },
  { id: 'line', label: 'Line', color: '#06C755', placeholder: 'my_store', fieldLabel: 'Line username', Icon: LineIcon },
  { id: 'viber', label: 'Viber', color: '#7360F2', placeholder: '+1 555-000-0000', fieldLabel: 'Viber number', Icon: ViberIcon },
  { id: 'snapchat', label: 'Snapchat', color: '#FFFC00', placeholder: 'my_store', fieldLabel: 'Snapchat username', Icon: SnapchatIcon },
  { id: 'wechat', label: 'WeChat', color: '#43DC71', placeholder: 'my_store', fieldLabel: 'WeChat ID', Icon: WeChatIcon },
  { id: 'linkedin', label: 'LinkedIn', color: '#0A66C2', placeholder: 'https://linkedin.com/in/you', fieldLabel: 'Profile URL', Icon: LinkedInIcon },
];

// ---- Common timezones ----
const WIZARD_TIMEZONES = [
  { value: "America/Los_Angeles", label: "Pacific Time (UTC-8)" },
  { value: "America/Denver", label: "Mountain Time (UTC-7)" },
  { value: "America/Chicago", label: "Central Time (UTC-6)" },
  { value: "America/New_York", label: "Eastern Time (UTC-5)" },
  { value: "America/Toronto", label: "Toronto (UTC-5)" },
  { value: "America/Vancouver", label: "Vancouver (UTC-8)" },
  { value: "America/Sao_Paulo", label: "Brasilia (UTC-3)" },
  { value: "UTC", label: "UTC / GMT" },
  { value: "Europe/London", label: "London (UTC+0/+1)" },
  { value: "Europe/Paris", label: "Paris, Berlin (UTC+1/+2)" },
  { value: "Europe/Moscow", label: "Moscow (UTC+3)" },
  { value: "Asia/Dubai", label: "Dubai (UTC+4)" },
  { value: "Asia/Kolkata", label: "Mumbai, New Delhi (UTC+5:30)" },
  { value: "Asia/Singapore", label: "Singapore (UTC+8)" },
  { value: "Asia/Tokyo", label: "Tokyo (UTC+9)" },
  { value: "Australia/Sydney", label: "Sydney (UTC+10)" },
  { value: "Pacific/Auckland", label: "Auckland (UTC+12)" },
];

const WIZARD_HOURS = Array.from({ length: 24 }, (_, i) => ({
  label: i === 0 ? '12:00 AM' : i === 12 ? '12:00 PM' : i > 12 ? `${i - 12}:00 PM` : `${i}:00 AM`,
  value: `${i}`,
}));

// ---- Step sub-components ----

function WelcomeStep({ isGrowthPlus, isAgentPlus }: {
  isGrowthPlus: boolean;
  isAgentPlus: boolean;
}) {
  type BadgeTone = 'info' | 'success' | undefined;
  const tierBadge: { label: string; tone: BadgeTone } = isAgentPlus
    ? { label: 'Agent+', tone: 'info' }
    : isGrowthPlus
      ? { label: 'Growth', tone: 'success' }
      : { label: 'Free', tone: undefined };

  const bullets = isAgentPlus
    ? [
      'Smart AI chatbot trained on your store content',
      'All channels, scheduling & branding control',
      'Knowledge base with auto-scan',
    ]
    : isGrowthPlus
      ? [
        'Branding removal & full button styling',
        'Scheduling: hours, lunch breaks & holidays',
        '90-day visitor analytics',
      ]
      : [
        'All channels, no visitor caps',
        'Auto-scheduling (timezone-aware)',
        '7-day analytics preview',
      ];

  return (
    <div style={{ textAlign: 'center', paddingBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <div style={{
          background: '#312e81',
          borderRadius: '12px',
          padding: '8px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <ReachLogo size="medium" showText={false} color="rgba(255,255,255,0.95)" detailColor="#312e81" />
        </div>
        <span style={{
          fontSize: '26px', fontWeight: 800, color: '#09090b',
          letterSpacing: '0.06em', lineHeight: 1,
          fontFamily: "'Inter', -apple-system, sans-serif",
        }}>
          REACH<span style={{ color: '#c084fc', fontSize: '32px', lineHeight: 0 }}>.</span>
        </span>
      </div>
      <div style={{ marginBottom: '12px' }}>
        <Badge tone={tierBadge.tone}>{tierBadge.label}</Badge>
      </div>
      <Text as="h2" variant="headingXl">Welcome! Let's get you set up.</Text>
      <div style={{ marginTop: '6px', marginBottom: '24px' }}>
        <Text as="p" tone="subdued">Your widget will be live in minutes.</Text>
      </div>
      <div style={{ textAlign: 'left', display: 'inline-block', minWidth: '260px' }}>
        <BlockStack gap="200">
          {bullets.map(b => (
            <InlineStack key={b} gap="200" blockAlign="center">
              <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 15, lineHeight: 1, flexShrink: 0 }}>✓</span>
              <Text as="p">{b}</Text>
            </InlineStack>
          ))}
        </BlockStack>
      </div>
    </div>
  );
}

// ---- Presets ----
const COLOR_PRESETS = [
  '#4f46e5', '#D81B60', '#10b981', '#f59e0b', '#3b82f6', '#09090b', '#7c3aed'
];

function ColorCircle({ color, selected, onClick }: { color: string; selected: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        width: 36, height: 36, borderRadius: '50%',
        border: `3px solid ${selected ? '#e2e8f0' : 'transparent'}`,
        cursor: 'pointer', transition: 'all 0.2s',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: selected ? 'white' : 'transparent',
        boxShadow: selected ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
      }}
    >
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: color, border: '1px solid rgba(0,0,0,0.1)' }} />
    </div>
  );
}

function ChannelStep({ channelId, channelValue, anchorColor, onChannelChange, onValueChange, onColorChange }: {
  channelId: string;
  channelValue: string;
  anchorColor: string;
  onChannelChange: (id: string) => void;
  onValueChange: (v: string) => void;
  onColorChange: (c: string) => void;
}) {
  const selected = CHANNEL_LIST.find(c => c.id === channelId) ?? CHANNEL_LIST[0];

  // Local state for the hex input to handle typing
  const [hexInput, setHexInput] = React.useState(anchorColor.toUpperCase());

  React.useEffect(() => {
    if (/^#[0-9A-F]{6}$/i.test(anchorColor)) {
      setHexInput(anchorColor.toUpperCase());
    }
  }, [anchorColor]);

  const handleHexChange = (v: string) => {
    let val = v.startsWith('#') ? v : `#${v}`;
    val = val.toUpperCase();
    if (/^#[0-9A-F]{0,6}$/i.test(val)) {
      setHexInput(val);
      if (val.length === 7) {
        onColorChange(val);
      }
    }
  };

  const isPhoneChannel = ['phone', 'whatsapp', 'sms'].includes(channelId);

  const handleValueChange = (v: string) => {
    if (isPhoneChannel) {
      // Strip everything except digits and leading +
      const cleaned = v.replace(/(?!^\+)\D/g, '');
      onValueChange(cleaned);
    } else {
      onValueChange(v);
    }
  };

  return (
    <BlockStack gap="500">
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">Where should customers reach you?</Text>
          <Text as="p" tone="subdued">Pick your primary contact channel. You can add more later.</Text>
        </BlockStack>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', maxHeight: '280px', overflowY: 'auto', padding: '4px' }}>
          {CHANNEL_LIST.map(ch => {
            const isSelected = ch.id === channelId;
            return (
              <button
                key={ch.id}
                onClick={() => { onChannelChange(ch.id); onValueChange(''); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                  padding: '12px 4px',
                  borderRadius: '16px', // Rounded edges for the hit area
                  background: isSelected ? `${ch.color}0f` : 'transparent',
                  border: 'none',
                  cursor: 'pointer', transition: 'all 0.15s',
                  outline: 'none',
                }}
              >
                <div style={{
                  width: 52, height: 52, borderRadius: '50%', // Round buttons
                  background: ch.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff',
                  flexShrink: 0,
                  border: isSelected ? '3px solid #fff' : 'none',
                  boxShadow: isSelected ? `0 0 0 2px ${ch.color}` : `0 2px 6px ${ch.color}30`,
                  transition: 'all 0.15s',
                }}>
                  <ch.Icon size={24} />
                </div>
                <span style={{ fontSize: 10, fontWeight: isSelected ? 600 : 500, color: isSelected ? ch.color : '#6b7280', lineHeight: 1.3, textAlign: 'center' }}>
                  {ch.label}
                </span>
              </button>
            );
          })}
        </div>
        <TextField
          label={selected.fieldLabel}
          value={channelValue}
          onChange={handleValueChange}
          placeholder={selected.placeholder}
          autoComplete="off"
          helpText={isPhoneChannel ? "Digits only. (e.g. +15551234455)" : "You can update this anytime in Settings."}
        />
      </BlockStack>

      <BlockStack gap="300">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">Widget button color</Text>
          <Text as="p" tone="subdued">Sets the color of your widget's launch button. Saved now, activates on Growth plan.</Text>
        </BlockStack>
        <InlineStack gap="300" blockAlign="center">
          {COLOR_PRESETS.map(c => (
            <ColorCircle key={c} color={c} selected={anchorColor === c} onClick={() => onColorChange(c)} />
          ))}
          <div style={{ marginLeft: '12px', paddingLeft: '20px', borderLeft: '1px solid #e5e7eb' }}>
            <InlineStack gap="200" blockAlign="center">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 6px',
                borderRadius: 20, border: '1px solid #d1d5db', background: 'white',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)', position: 'relative'
              }}>
                <div
                  onClick={() => {
                    const input = document.getElementById('onboarding-color-input');
                    input?.click();
                  }}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: /^#[0-9A-F]{6}$/i.test(anchorColor) ? anchorColor : '#4f46e5',
                    border: '1px solid rgba(0,0,0,0.14)', flexShrink: 0, cursor: 'pointer'
                  }}
                />
                <div style={{ width: '75px' }}>
                  <input
                    type="text"
                    value={hexInput}
                    spellCheck={false}
                    onChange={e => handleHexChange(e.target.value)}
                    style={{
                      fontSize: 12, fontWeight: 600, color: '#374151',
                      fontFamily: 'monospace', border: 'none', background: 'transparent',
                      width: '100%', padding: 0, outline: 'none'
                    }}
                  />
                </div>
                <input
                  id="onboarding-color-input"
                  type="color"
                  value={/^#[0-9A-F]{6}$/i.test(anchorColor) ? anchorColor : '#4f46e5'}
                  onChange={e => onColorChange(e.target.value)}
                  style={{ position: 'absolute', opacity: 0, width: 1, height: 1, top: 0, left: 0, pointerEvents: 'none' }}
                />
              </div>
              <Text as="p" variant="bodySm" tone="subdued">or enter Hex</Text>
            </InlineStack>
          </div>
        </InlineStack>
      </BlockStack>
    </BlockStack>
  );
}

function ScheduleStep({ timezone, startHour, endHour, onTimezone, onStartHour, onEndHour }: {
  timezone: string;
  startHour: number;
  endHour: number;
  onTimezone: (v: string) => void;
  onStartHour: (v: number) => void;
  onEndHour: (v: number) => void;
}) {
  const safeTimezone = WIZARD_TIMEZONES.some(t => t.value === timezone) ? timezone : 'UTC';
  return (
    <BlockStack gap="400">
      <BlockStack gap="100">
        <Text as="h2" variant="headingMd">Set your business hours</Text>
        <Text as="p" tone="subdued">Customers will see your live status (open / closed) based on these hours.</Text>
      </BlockStack>
      <Select
        label="Timezone"
        options={WIZARD_TIMEZONES}
        value={safeTimezone}
        onChange={onTimezone}
      />
      <InlineStack gap="400">
        <div style={{ flex: 1 }}>
          <Select
            label="Opens at"
            options={WIZARD_HOURS}
            value={`${startHour}`}
            onChange={v => onStartHour(parseInt(v, 10))}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Select
            label="Closes at"
            options={WIZARD_HOURS}
            value={`${endHour}`}
            onChange={v => onEndHour(parseInt(v, 10))}
          />
        </div>
      </InlineStack>
    </BlockStack>
  );
}

function ChatbotStep({ botName, welcomeMsg, onBotName, onWelcomeMsg }: {
  botName: string;
  welcomeMsg: string;
  onBotName: (v: string) => void;
  onWelcomeMsg: (v: string) => void;
}) {
  return (
    <BlockStack gap="400">
      <BlockStack gap="100">
        <Text as="h2" variant="headingMd">Set up your Chatbot Assistant</Text>
        <Text as="p" tone="subdued">Your chatbot handles questions automatically, 24/7.</Text>
        <Text as="p" tone="subdued">All settings can be changed later in the Chatbot section.</Text>
      </BlockStack>
      <TextField
        label="Assistant Name"
        value={botName}
        onChange={onBotName}
        placeholder="e.g. Adam"
        autoComplete="off"
        helpText="Shown to customers as the chatbot's name."
      />
      <TextField
        label="Initial Greeting"
        value={welcomeMsg}
        onChange={onWelcomeMsg}
        multiline={2}
        autoComplete="off"
        helpText="The first message customers see when they open the chat."
      />
      <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb' }}>
        <Text as="p" variant="bodySm" tone="subdued">Preview</Text>
        <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', background: '#09090b',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
              {(botName || 'A')[0].toUpperCase()}
            </span>
          </div>
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
            padding: '8px 12px', maxWidth: '280px',
          }}>
            <Text as="p" variant="bodySm">{welcomeMsg || 'Hi! How can I help you today?'}</Text>
          </div>
        </div>
      </div>
    </BlockStack>
  );
}

// ---- Main Wizard ----

const STEP_LABELS: Record<string, string> = {
  welcome: 'Welcome',
  channel: 'Channel',
  schedule: 'Hours',
  chatbot: 'Chatbot',
};

export function OnboardingWizard({ planTier, shopDomain: _shopDomain, onComplete, onDismiss }: OnboardingWizardProps) {
  const level = tierIndex(planTier);
  const isGrowthPlus = level >= 1;
  const isAgentPlus = level >= 2;

  const steps = [
    'welcome',
    'channel',
    ...(isGrowthPlus ? ['schedule'] : []),
    ...(isAgentPlus ? ['chatbot'] : []),
  ];

  const [step, setStep] = useState(0);
  const [channelId, setChannelId] = useState('whatsapp');
  const [channelValue, setChannelValue] = useState('');
  const [anchorColor, setAnchorColor] = useState('#000000');
  const [timezone, setTimezone] = useState(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return WIZARD_TIMEZONES.some(t => t.value === tz) ? tz : 'UTC';
    } catch { return 'UTC'; }
  });
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(17);
  const [botName, setBotName] = useState('Adam');
  const [welcomeMsg, setWelcomeMsg] = useState('Hi! How can I help you today?');

  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const current = steps[step];

  const handleNext = () => {
    if (isLast) {
      onComplete({
        channelId,
        channelValue,
        anchorColor,
        timezone: isGrowthPlus ? timezone : undefined,
        startHour: isGrowthPlus ? startHour : undefined,
        endHour: isGrowthPlus ? endHour : undefined,
        botName: isAgentPlus ? botName : undefined,
        welcomeMsg: isAgentPlus ? welcomeMsg : undefined,
      });
    } else {
      setStep((s: number) => s + 1);
    }
  };

  return (
    <Modal open onClose={onDismiss} title="Set up Reach" size="large">
      <Modal.Section>
        {/* Step indicator */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginBottom: '32px' }}>
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div
                onClick={() => i < step && setStep(i)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: i < step ? 'pointer' : 'default' }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: i <= step ? '#312e81' : '#e5e7eb',
                  color: i <= step ? '#fff' : '#9ca3af',
                  fontSize: 12, fontWeight: 700, transition: 'background 0.2s', flexShrink: 0,
                }}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span style={{
                  fontSize: 10,
                  color: i === step ? '#312e81' : '#9ca3af',
                  fontWeight: i === step ? 600 : 400,
                }}>
                  {STEP_LABELS[s]}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div style={{
                  height: 1, flex: 1, background: i < step ? '#312e81' : '#e5e7eb',
                  marginBottom: '18px', maxWidth: '48px',
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        {current === 'welcome' && (
          <WelcomeStep isGrowthPlus={isGrowthPlus} isAgentPlus={isAgentPlus} />
        )}
        {current === 'channel' && (
          <ChannelStep
            channelId={channelId}
            channelValue={channelValue}
            anchorColor={anchorColor}
            onChannelChange={setChannelId}
            onValueChange={setChannelValue}
            onColorChange={setAnchorColor}
          />
        )}
        {current === 'schedule' && (
          <ScheduleStep
            timezone={timezone}
            startHour={startHour}
            endHour={endHour}
            onTimezone={setTimezone}
            onStartHour={setStartHour}
            onEndHour={setEndHour}
          />
        )}
        {current === 'chatbot' && (
          <ChatbotStep
            botName={botName}
            welcomeMsg={welcomeMsg}
            onBotName={setBotName}
            onWelcomeMsg={setWelcomeMsg}
          />
        )}

        {/* Navigation */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #e5e7eb',
        }}>
          {!isFirst && !isLast ? (
            <button
              onClick={() => setStep(steps.length - 1)}
              style={{
                background: 'none', border: 'none', color: '#9ca3af',
                cursor: 'pointer', fontSize: 14, padding: '8px 0',
                fontFamily: 'inherit',
              }}
            >
              Skip to finish →
            </button>
          ) : (
            <div />
          )}
          <InlineStack gap="200">
            {!isFirst && (
              <Button onClick={() => setStep((s: number) => s - 1)}>Back</Button>
            )}
            <Button variant="primary" onClick={handleNext}>
              {isLast ? 'Finish setup' : 'Next →'}
            </Button>
          </InlineStack>
        </div>
      </Modal.Section>
    </Modal>
  );
}
